import { Box, IconButton, Tooltip } from '@mui/material';
import { Info } from 'lucide-react';
import { MouseEvent, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import ObjectDetailsDialog, {
    ObjectDetails,
    ObjectDetailsDialogState,
    useObjectDetailsDialog,
} from './ObjectDetailsDialog';

interface ObjectInfoButtonProps extends ObjectDetails {
    /** The dialog state, when the row holds it; otherwise the button's own. */
    dialog?: ObjectDetailsDialogState;
    'data-testid'?: string;
}

/**
 * The (i) that opens an object's details and stays open until dismissed.
 * Not a tooltip: reading a UID, checking it against PACS and stepping into
 * the raw DICOM all outlast a panel that closes when the pointer moves.
 */
export default function ObjectInfoButton({
    dialog: rowDialog,
    'data-testid': testId,
    ...details
}: ObjectInfoButtonProps) {
    const intl = useIntl();
    const ownDialog = useObjectDetailsDialog();
    const dialog = rowDialog ?? ownDialog;

    const handleOpen = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            // These buttons sit inside SelectableListItem rows whose own
            // onClick selects the object. Asking for details must not also
            // change what the user is working on.
            event.stopPropagation();
            dialog.open();
        },
        [dialog]
    );

    const label = intl.formatMessage({ id: 'properties.show' });

    return (
        <>
            <Tooltip title={label}>
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    aria-label={label}
                    aria-haspopup="dialog"
                    data-testid={testId ?? 'object-info-button'}
                    sx={{ p: 0.25, color: 'primary.main', flexShrink: 0 }}
                >
                    <Info size={12} />
                </IconButton>
            </Tooltip>
            <ObjectDetailsDialog dialog={dialog} {...details} />
        </>
    );
}

interface ObjectRowProps {
    /** The row's ObjectInfoButton; falsy when the object has no details. */
    info?: ReactNode;
    children: ReactNode;
    'data-testid'?: string;
}

/**
 * Shell shared by the object list rows: the content takes the slack, the
 * trailing (i) button keeps the right edge. One place, so the row
 * components cannot drift on this layout.
 */
export function ObjectRow({
    info,
    children,
    'data-testid': testId,
}: ObjectRowProps) {
    return (
        <Box
            display="flex"
            alignItems="flex-start"
            gap={0.5}
            width="100%"
            overflow="hidden"
            data-testid={testId}
        >
            <Box flex={1} minWidth={0}>
                {children}
            </Box>
            {info}
        </Box>
    );
}
